import apiClient from './api';

interface RefundApplication {
  id: string;
  orderId: string;
  reason: string;
  amount: number;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  updatedAt: string;
}

// Server response format
interface RefundHistoryResponse {
  id: string;
  subscription_id: string;
  reason: string;
  undelivered_count: number;
  estimated_amount: number;
  status: string;
  created_at: string;
  updated_at: string;
}


const refundService = {
  // Get all refund applications for the current user
  getRefundApplications: async (): Promise<RefundApplication[]> => {
    try {
      // Real API call
      const response = await apiClient.get('/refunds/history');
      
      // Transform server response to client format
      if (response.data && response.data.data) {
        return response.data.data.map((item: RefundHistoryResponse) => ({
          id: item.id,
          orderId: item.subscription_id,
          reason: item.reason,
          amount: item.estimated_amount,
          status: item.status,
          createdAt: item.created_at,
          updatedAt: item.updated_at
        }));
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching refund history:', error);
      throw error;
    }
  },

  // Get a specific refund application by ID
  getRefundApplication: async (id: string): Promise<RefundApplication> => {
    
    const response = await apiClient.get(`/refund-applications/${id}`);
    return response.data;
  },

  // Create a new refund application
  createRefundApplication: async (
    orderId: string,
    reason: string,
    amount: number,
    images?: string[]
  ): Promise<RefundApplication> => {
    try {
      // First, get the current subscription details to access remaining_deliveries
      const currentSubResponse = await apiClient.get('/subscriptions/current');
      
      let undeliveredCount = 0;
      
      if (currentSubResponse.data && currentSubResponse.data.data) {
        // Use the remaining_deliveries from the subscription data
        undeliveredCount = currentSubResponse.data.data.remainingDeliveries || 0;
      } else {
        // Fallback calculation if subscription data is not available
        undeliveredCount = Math.ceil(amount / 100);
      }
      
      // Real API call to create the refund request
      const response = await apiClient.post('/refunds/create', {
        subscriptionId: orderId,
        reason: reason,
        undeliveredCount: undeliveredCount,
        estimatedAmount: amount,
        images: images
      });
      
      if (response.data && response.data.data) {
        // Transform server response to client format
        return {
          id: response.data.data.id,
          orderId: response.data.data.subscription_id,
          reason: reason,
          amount: amount,
          status: response.data.data.status,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
      }
      
      throw new Error('Invalid server response');
    } catch (error) {
      console.error('Error creating refund application:', error);
      throw error;
    }
  },
};

export default refundService; 